import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit2, CheckCircle, XCircle, Users, Building2, BookOpen, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'
import { plansApi, subscriptionPromotionsApi } from '../../lib/api'
import type { Plan } from '../../types'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatMoney } from '../../lib/utils'
import PlanBadge from '../../components/shared/PlanBadge'
import LoadingState from '../../components/shared/LoadingState'

import { tx } from '../../lib/i18n'
const defaultForm = (): Partial<Plan> => ({
  displayName: '', description: '', priceMonthly: 0, priceYearly: 0, isActive: true,
  branchLimit: 1, staffLimit: 5, bookingLimit: null, customerLimit: null,
  hasPOS: false, hasCRM: false, hasMemberships: false, hasReports: false,
  hasMultiBranch: false, hasAICenter: false, hasNotificationAutomation: false,
})

const featureLabels: { key: keyof Plan; label: string }[] = [
  { key: 'hasPOS', label: tx('POS System') },
  { key: 'hasCRM', label: 'CRM' },
  { key: 'hasMemberships', label: tx('Memberships') },
  { key: 'hasReports', label: tx('Reports & Analytics') },
  { key: 'hasMultiBranch', label: tx('Multi-Branch') },
  { key: 'hasAICenter', label: tx('AI Center') },
  { key: 'hasNotificationAutomation', label: tx('Notification Automation') },
]

/** Promotions that currently apply to one plan + a shortcut to create one for it. */
const PlanPromos: React.FC<{ planId: string }> = ({ planId }) => {
  const { can } = usePermission()
  const { data: promos = [] } = useQuery({ queryKey: ['subscription-promotions'], queryFn: () => subscriptionPromotionsApi.list().then((r) => r.data.data), enabled: can('subscription-promotions.view') })
  const now = Date.now()
  const mine = promos.filter((p) => p.status === 'active' && p.plans.some((x) => x.id === planId) && new Date(p.startsAt).getTime() <= now && (!p.endsAt || new Date(p.endsAt).getTime() > now))
  if (!can('subscription-promotions.view')) return null
  return (
    <div className="rounded-xl border border-dashed border-rose-200 bg-rose-50/40 p-3" data-testid="plan-promos">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-rose-700">{tx('Promotions running now')}</p>
        {can('subscription-promotions.create') && (
          <Link to={`/subscription-promotions?plan=${planId}`} className="text-xs font-medium text-indigo-600 hover:underline" data-testid="plan-add-promo">+ {tx('Create a promotion for this plan')}</Link>
        )}
      </div>
      {mine.length === 0 ? (
        <p className="mt-1 text-xs text-gray-500">{tx('None — shops pay the full price.')}</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {mine.map((p) => (
            <li key={p.id} className="text-xs text-gray-700" data-testid="plan-promo-row">
              <span className="font-mono font-semibold">{p.code}</span> · {p.discountType === 'percent' ? `-${p.discountValue}%` : `-฿${(p.discountValue / 100).toLocaleString()}`}
              {' · '}{p.benefitCycles > 1 ? `${p.benefitCycles} ${tx('months')}` : tx('First bill only')}
              {' · '}{p.autoApply ? tx('automatic (no code)') : tx('code typed at checkout')}
              {p.endsAt ? ` · ${tx('until')} ${new Date(p.endsAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const PlanCard: React.FC<{ plan: Plan; onEdit: (plan: Plan) => void }> = ({ plan, onEdit }) => {
  const features = featureLabels.filter((f) => plan[f.key])
  const missing = featureLabels.filter((f) => !plan[f.key])
  const now = Date.now()
  const activePrice = plan.priceVersions?.filter((p) => p.isActive && new Date(p.effectiveAt).getTime() <= now).sort((a, b) => new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime())[0]
  const { can } = usePermission()
  const nextPrice = plan.priceVersions?.filter((p) => p.isActive && new Date(p.effectiveAt).getTime() > now).sort((a, b) => new Date(a.effectiveAt).getTime() - new Date(b.effectiveAt).getTime())[0]

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col gap-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <PlanBadge plan={plan.name} />
          <h3 className="mt-2 text-lg font-bold text-gray-900">{plan.displayName}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{plan.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {plan.isActive ? (
            <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{tx('Active')}</span>
          ) : (
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{tx('Inactive')}</span>
          )}
          {can('plans.update') && (
          <button
            onClick={() => onEdit(plan)}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          )}
        </div>
      </div>

      {/* Pricing */}
      <div className="flex gap-3">
        <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-400">{tx('Monthly')}</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">{formatMoney(activePrice ? activePrice.monthlyAmountSatang / 100 : plan.priceMonthly)}</p>
        </div>
        <div className="flex-1 bg-indigo-50 rounded-xl p-3 text-center">
          <p className="text-xs text-indigo-500">{tx('Yearly')}</p>
          <p className="text-lg font-bold text-indigo-700 mt-0.5">{formatMoney(activePrice ? activePrice.yearlyAmountSatang / 100 : plan.priceYearly)}</p>
        </div>
      </div>
      <PlanPromos planId={plan.id} />
      {nextPrice && <p className="text-xs text-amber-700">{tx('Next price:')} {formatMoney(nextPrice.monthlyAmountSatang / 100)}/mo from {new Date(nextPrice.effectiveAt).toLocaleDateString()}</p>}

      {/* Limits */}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="h-3.5 w-3.5 text-gray-400" />
          <span>{plan.branchLimit >= 9999 ? 'Unlimited' : plan.branchLimit} branches</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="h-3.5 w-3.5 text-gray-400" />
          <span>{plan.staffLimit >= 9999 ? 'Unlimited' : plan.staffLimit} staff</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <BookOpen className="h-3.5 w-3.5 text-gray-400" />
          <span>{plan.bookingLimit == null ? 'Unlimited' : plan.bookingLimit} {tx('bookings/mo')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="h-3.5 w-3.5 text-gray-400" />
          <span>{plan.customerLimit == null ? 'Unlimited' : plan.customerLimit} customers</span>
        </div>
      </div>

      {/* Features */}
      <div className="space-y-1.5">
        {features.map((f) => (
          <div key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
            <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
            {f.label}
          </div>
        ))}
        {missing.slice(0, 2).map((f) => (
          <div key={f.key} className="flex items-center gap-2 text-sm text-gray-400">
            <XCircle className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
            {f.label}
          </div>
        ))}
      </div>
    </div>
  )
}

type FormErrors = Partial<Record<keyof Plan, string>>

const validatePlanForm = (form: Partial<Plan>): FormErrors => {
  const errors: FormErrors = {}
  if (!form.displayName?.trim()) errors.displayName = 'Required'
  if (!form.description?.trim()) errors.description = 'Required'
  if (form.priceMonthly == null || isNaN(form.priceMonthly) || form.priceMonthly <= 0) errors.priceMonthly = 'Must be greater than 0'
  if (form.priceYearly == null || isNaN(form.priceYearly) || form.priceYearly <= 0) errors.priceYearly = 'Must be greater than 0'
  if (!form.branchLimit || form.branchLimit < 1) errors.branchLimit = 'Min 1'
  if (!form.staffLimit || form.staffLimit < 1) errors.staffLimit = 'Min 1'
  const hasFeature = featureLabels.some((f) => !!form[f.key])
  if (!hasFeature) errors.hasPOS = 'Select at least 1 feature'
  return errors
}

const inputCls = (err?: string) =>
  `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
    err
      ? 'border-red-400 bg-red-50 focus:ring-red-400'
      : 'border-gray-200 focus:ring-indigo-500'
  }`

const Req: React.FC = () => <span className="text-red-500 ml-0.5">*</span>

const PlanFormModal: React.FC<{
  plan: Partial<Plan> | null
  onClose: () => void
  onSave: (data: Partial<Plan>) => void
  isLoading?: boolean
}> = ({ plan, onClose, onSave, isLoading }) => {
  const [form, setForm] = useState<Partial<Plan>>(plan ?? defaultForm())
  // '' = the new price (if changed) applies right away. The picker is local time — never pre-fill it
  // with toISOString() (that's UTC → 7 hours in the past in Thailand, and the server rejects past dates).
  const [priceEffectiveAt, setPriceEffectiveAt] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})

  const set = (key: keyof Plan, value: unknown) => {
    setForm((f) => ({ ...f, [key]: value }))
    setErrors((e) => ({ ...e, [key]: undefined }))
  }

  const handleSave = () => {
    const errs = validatePlanForm(form)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    onSave({ ...form, priceEffectiveAt } as Partial<Plan>)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-5">
          {plan?.id ? `Edit Plan — ${plan.displayName}` : tx('Create Plan')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Display Name')}<Req /></label>
            <input
              type="text"
              value={form.displayName ?? ''}
              onChange={(e) => set('displayName', e.target.value)}
              className={inputCls(errors.displayName)}
            />
            {errors.displayName && <p className="text-xs text-red-500 mt-1">{errors.displayName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Description')}<Req /></label>
            <input
              type="text"
              value={form.description ?? ''}
              onChange={(e) => set('description', e.target.value)}
              className={inputCls(errors.description)}
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price Monthly (฿)<Req /></label>
              <input
                type="number"
                value={form.priceMonthly ?? 0}
                onChange={(e) => set('priceMonthly', parseFloat(e.target.value))}
                className={inputCls(errors.priceMonthly)}
              />
              {errors.priceMonthly && <p className="text-xs text-red-500 mt-1">{errors.priceMonthly}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price Yearly (฿)<Req /></label>
              <input
                type="number"
                value={form.priceYearly ?? 0}
                onChange={(e) => set('priceYearly', parseFloat(e.target.value))}
                className={inputCls(errors.priceYearly)}
              />
              {errors.priceYearly && <p className="text-xs text-red-500 mt-1">{errors.priceYearly}</p>}
            </div>
          </div>
          {plan?.id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('New price effective from')}</label>
              <input type="datetime-local" value={priceEffectiveAt} onChange={(e) => setPriceEffectiveAt(e.target.value)} className={inputCls()} data-testid="plan-price-effective" />
              <p className="mt-1 text-xs text-gray-400">เว้นว่าง = ถ้าแก้ราคา ราคาใหม่มีผลทันที · ถ้าไม่ได้แก้ราคา จะไม่สร้างราคาใหม่</p>
              <p className="mt-1 text-xs text-gray-400">{tx('Existing subscribers keep their locked price. This price applies to new subscriptions after this time.')}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Branch Limit')}<Req /></label>
              <input type="number" value={form.branchLimit ?? 1} onChange={(e) => set('branchLimit', parseInt(e.target.value))} className={inputCls(errors.branchLimit)} />
              {errors.branchLimit && <p className="text-xs text-red-500 mt-1">{errors.branchLimit}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Staff Limit')}<Req /></label>
              <input type="number" value={form.staffLimit ?? 5} onChange={(e) => set('staffLimit', parseInt(e.target.value))} className={inputCls(errors.staffLimit)} />
              {errors.staffLimit && <p className="text-xs text-red-500 mt-1">{errors.staffLimit}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Booking Limit')}</label>
              <input type="number" value={form.bookingLimit ?? ''} placeholder="unlimited" onChange={(e) => set('bookingLimit', e.target.value ? parseInt(e.target.value) : null)} className={inputCls()} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Customer Limit')}</label>
              <input type="number" value={form.customerLimit ?? ''} placeholder="unlimited" onChange={(e) => set('customerLimit', e.target.value ? parseInt(e.target.value) : null)} className={inputCls()} />
            </div>
          </div>
          <div>
            <p className={`text-sm font-medium mb-2 ${errors.hasPOS ? 'text-red-500' : 'text-gray-700'}`}>
              {tx('Features')}<Req />
            </p>
            <div className={`grid grid-cols-2 gap-2 rounded-lg p-2 ${errors.hasPOS ? 'bg-red-50 border border-red-400' : ''}`}>
              {featureLabels.map((f) => (
                <label key={f.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form[f.key]}
                    onChange={(e) => {
                      set(f.key, e.target.checked)
                      setErrors((prev) => ({ ...prev, hasPOS: undefined }))
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">{f.label}</span>
                </label>
              ))}
            </div>
            {errors.hasPOS && <p className="text-xs text-red-500 mt-1">{errors.hasPOS}</p>}
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-700">{tx('Plan is Active')}</span>
          </label>
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isLoading ? tx('Saving...') : plan?.id ? 'Save Changes' : tx('Create Plan')}
          </button>
        </div>
      </div>
    </div>
  )
}

const PlansPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()
  const [editingPlan, setEditingPlan] = useState<Plan | null | 'new'>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansApi.list().then((r) => r.data.data),
  })

  const plans = [...(data ?? [])].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

  const updateMutation = useMutation({
    mutationFn: async (form: Partial<Plan>) => {
      if (!editingPlan || editingPlan === 'new') return plansApi.create(form as Parameters<typeof plansApi.create>[0]).then((r) => r.data)
      const scheduled = form as Partial<Plan> & { priceEffectiveAt?: string }
      const { priceMonthly, priceYearly, priceEffectiveAt, ...planFields } = scheduled
      await plansApi.update(editingPlan.id, planFields).then((r) => r.data)
      // Only schedule a price version when the price actually changed or a start time was picked —
      // saving a description must not create a new (identical) price version.
      const priceChanged = Number(priceMonthly) !== Number(editingPlan.priceMonthly) || Number(priceYearly) !== Number(editingPlan.priceYearly)
      if (priceMonthly != null && priceYearly != null && (priceChanged || priceEffectiveAt)) {
        const at = priceEffectiveAt ? new Date(priceEffectiveAt) : null
        await plansApi.schedulePrice(editingPlan.id, {
          priceMonthly,
          priceYearly,
          effectiveAt: at && at.getTime() > Date.now() ? at.toISOString() : undefined,
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] })
      showToast({ type: 'success', message: editingPlan === 'new' ? 'Plan created.' : 'Plan updated.' })
      setEditingPlan(null)
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to save plan.') }),
  })

  if (isLoading) return <LoadingState variant="spinner" message={tx('Loading plans...')} />

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Plans')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('Manage subscription plans and features')}</p>
        </div>
        {can('plans.create') && (
        <button
          onClick={() => setEditingPlan('new')}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          {tx('Create Plan')}
        </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} onEdit={setEditingPlan} />
        ))}
      </div>

      {editingPlan !== null && (
        <PlanFormModal
          plan={editingPlan === 'new' ? null : editingPlan}
          onClose={() => setEditingPlan(null)}
          onSave={(form) => updateMutation.mutate(form)}
          isLoading={updateMutation.isPending}
        />
      )}
    </div>
  )
}

export default PlansPage
